// src/encounter/device-trends/device-trends-slot.tsx
// The one place that turns `useWearableTrends` into what a host draws: nothing while the `wearables` switch is off,
// nothing for "hidden" (no consent to share, or no connected device: ruling, 30 Oct), otherwise the card in its
// loading / error / empty / ready state. Hosts pass only the patient and the variant.
// `?x=device-card-catalog` (ON on dev): the card is Antonin's own DeviceTrendsCard from @scripsteam/signal (D-79), fed through
// device-trends-catalog.ts; the PA draws none of its look. In the chart column his section sits on the rail's 16 inset, as
// in his catalog frame (layout only). OFF = the PA card below, unchanged.
import { lazy, Suspense } from 'react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { isWearablesOn, useWearableTrends } from '../services/use-wearable-trends';
import { DeviceTrendsCard } from './device-trends-card';
import { toCatalogSeries } from './device-trends-catalog';

type SlotProps = { patientId: string | null | undefined; variant: 'card' | 'section' };

// His card, styles and scoped tokens load on demand only: a static import would put the whole catalog in every user's
// first download, switch on or off (Codex on #920: initial JS 4.6 MB -> 10.2 MB).
const CatalogDeviceTrendsCard = lazy(() => import('./device-trends-catalog-card'));

export function DeviceTrendsSlot(props: SlotProps) {
  // Switch off: nothing is mounted, so nothing is read (and no query client is needed).
  return isWearablesOn() && props.patientId ? <DeviceTrendsSlotOn {...props} /> : null;
}

function DeviceTrendsSlotOn(props: SlotProps) {
  return isEncounterFlagOn('deviceCardCatalog') ? <CatalogSlot {...props} /> : <PaSlot {...props} />;
}

function CatalogSlot({ patientId, variant }: SlotProps) {
  const trends = useWearableTrends(patientId);
  if (!trends.on) return null;
  const card = (() => {
    if (trends.isError) return <CatalogDeviceTrendsCard state="error" variant={variant} series={[]} devices={[]} onRetry={() => void trends.refetch()} />;
    if (trends.isPending) return <CatalogDeviceTrendsCard state="loading" variant={variant} series={[]} devices={[]} />;
    const data = trends.data;
    if (data.kind === 'hidden') return null;
    if (data.kind === 'empty') return <CatalogDeviceTrendsCard state="empty" variant={variant} series={[]} devices={data.devices} />;
    // In the left rail with `railFixes`, the sync line is date-only, as the PA card draws it there.
    const datesOnly = variant === 'section' && isEncounterFlagOn('railFixes');
    return <CatalogDeviceTrendsCard state="ready" variant={variant} series={toCatalogSeries(data.series, { datesOnly })} devices={[]} />;
  })();
  return card ? <Suspense fallback={null}>{card}</Suspense> : null;
}

function PaSlot({ patientId, variant }: SlotProps) {
  const trends = useWearableTrends(patientId);
  if (!trends.on) return null;
  if (trends.isError) return <DeviceTrendsCard state="error" variant={variant} onRetry={() => void trends.refetch()} />;
  if (trends.isPending) return <DeviceTrendsCard state="loading" variant={variant} />;
  const data = trends.data;
  if (data.kind === 'hidden') return null;
  if (data.kind === 'empty') return <DeviceTrendsCard state="empty" variant={variant} devices={data.devices} />;
  return <DeviceTrendsCard state="ready" variant={variant} series={data.series} />;
}
